import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { SearchHit, SearchKind, SearchResponse, Locale } from '@monrdv/shared';
import { SEARCH_KIND_ORDER, formatMoney, formatPhone, hitPath, looksLikePhone } from '@monrdv/shared';

/**
 * Global search.
 *
 * One box that searches names, addresses, phone numbers, emails and document numbers at
 * once, and can answer "what is near me" from the device's position. Results arrive as
 * cards carrying everything they display, so opening the dropdown is a single round trip.
 *
 * Interaction rules that matter more than they look:
 *
 *   - **The query lives in the URL.** Reload, back, and a pasted link all restore the same
 *     search. Nothing here is component state that a refresh would lose.
 *   - **Groups keep a fixed order.** Results never reshuffle between keystrokes; aiming for
 *     a row that moves is the fastest way to make search feel broken.
 *   - **Keyboard first.** ⌘K opens, arrows move through the flat list across groups, Enter
 *     opens, Escape closes. A coordinator on the phone should never need the mouse.
 */

export type GlobalSearchProps = {
  /** Current query, owned by the router so it survives a reload. */
  query: string;
  onQueryChange: (next: string) => void;
  /** Results for `query`, or undefined while loading. */
  results?: SearchResponse;
  isLoading: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onNavigate: (path: string) => void;
  /** Called when the user asks for work near their current position. */
  onSearchNearby?: (position: { latitude: number; longitude: number }) => void;
  locale: Locale;
  t: (key: string, vars?: Record<string, string | number>) => string;
};

export function GlobalSearch({
  query,
  onQueryChange,
  results,
  isLoading,
  open,
  onOpenChange,
  onNavigate,
  onSearchNearby,
  locale,
  t,
}: GlobalSearchProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [locating, setLocating] = useState(false);

  // Flatten the grouped results into the order they are displayed, so arrow keys walk the
  // list the eye follows rather than the order the server happened to return.
  const flat = useMemo(() => {
    if (!results?.groups) return results?.hits ?? [];
    const byKind = new Map(results.groups.map((g) => [g.kind, g.hits]));
    return SEARCH_KIND_ORDER.flatMap((kind) => byKind.get(kind) ?? []);
  }, [results]);

  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  // ⌘K / Ctrl+K from anywhere. Registered once on the document because the shortcut has to
  // work while focus is inside a table, a form, or a message thread.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        onOpenChange(true);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onOpenChange]);

  const openHit = useCallback(
    (hit: SearchHit) => {
      onNavigate(hitPath(hit));
      onOpenChange(false);
    },
    [onNavigate, onOpenChange],
  );

  const onInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, Math.max(flat.length - 1, 0)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      const hit = flat[activeIndex];
      if (hit) {
        e.preventDefault();
        openHit(hit);
      }
    } else if (e.key === 'Escape') {
      onOpenChange(false);
    }
  };

  const requestNearby = () => {
    if (!onSearchNearby || !('geolocation' in navigator)) return;
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLocating(false);
        onSearchNearby({ latitude: pos.coords.latitude, longitude: pos.coords.longitude });
      },
      // A refused or unavailable fix is not an error state worth interrupting anyone for;
      // the button simply stops spinning and text search still works.
      () => setLocating(false),
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 60_000 },
    );
  };

  const showNearby = Boolean(onSearchNearby) && 'geolocation' in navigator;

  return (
    <>
      {/* Always-visible trigger in the app bar. Shows the shortcut, because a keyboard
          shortcut nobody knows about does not exist. */}
      <button
        type="button"
        onClick={() => onOpenChange(true)}
        className="search-trigger"
        aria-label={t('common.search')}
      >
        <SearchIcon />
        <span className="search-trigger__text">{t('common.searchPlaceholder')}</span>
        <kbd className="search-trigger__kbd">⌘K</kbd>
      </button>

      {open && (
        <div
          className="search-scrim"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) onOpenChange(false);
          }}
        >
          <div className="search-panel" role="dialog" aria-modal="true" aria-label={t('common.search')}>
            <div className="search-field">
              <SearchIcon />
              <input
                ref={inputRef}
                value={query}
                onChange={(e) => onQueryChange(e.target.value)}
                onKeyDown={onInputKeyDown}
                placeholder={t('common.searchPlaceholder')}
                // Browser autofill over a search box is actively harmful.
                autoComplete="off"
                spellCheck={false}
                enterKeyHint="search"
                role="combobox"
                aria-expanded
                aria-controls="search-results"
                aria-activedescendant={flat[activeIndex] ? `hit-${flat[activeIndex]!.id}` : undefined}
              />
              {showNearby && (
                <button
                  type="button"
                  className="search-nearby"
                  onClick={requestNearby}
                  disabled={locating}
                  title={t('search.nearby')}
                >
                  <PinIcon />
                  {t('search.nearby')}
                </button>
              )}
            </div>

            <div className="search-results" id="search-results" role="listbox">
              {!query.trim() && <p className="search-hint">{t('search.hint')}</p>}

              {query.trim() && !isLoading && flat.length === 0 && (
                <p className="search-hint">{t('common.noResults')}</p>
              )}

              {results?.groups?.map((group) => (
                <section key={group.kind} className="search-group">
                  <header className="label search-group__header">
                    {t(`search.groups.${group.kind}`)}
                    {group.total > group.hits.length && (
                      <span className="search-group__count">{group.total}</span>
                    )}
                  </header>
                  {group.hits.map((hit) => {
                    const index = flat.indexOf(hit);
                    return (
                      <ResultCard
                        key={`${hit.kind}-${hit.id}`}
                        hit={hit}
                        active={index === activeIndex}
                        onSelect={() => openHit(hit)}
                        onHover={() => setActiveIndex(index)}
                        locale={locale}
                        t={t}
                      />
                    );
                  })}
                </section>
              ))}

              {/* A tiny, honest performance readout. Coordinators live in this box all day
                  and a visible timing builds trust that it is actually fast. */}
              {results && flat.length > 0 && (
                <footer className="search-footer">
                  <span>
                    {flat.length} · {results.tookMs} ms
                  </span>
                  <span className="search-footer__keys">
                    <kbd>↑</kbd>
                    <kbd>↓</kbd> naviguer <kbd>↵</kbd> ouvrir <kbd>esc</kbd> fermer
                  </span>
                </footer>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/**
 * One result card.
 *
 * Each kind shows the two or three facts that identify it in the real world — a customer by
 * address and phone, an invoice by what is still owed. Generic "title + subtitle" rows would
 * make a customer and an invoice look identical, which is exactly what makes a search list
 * slow to read.
 */
function ResultCard({
  hit,
  active,
  onSelect,
  onHover,
  locale,
  t,
}: {
  hit: SearchHit;
  active: boolean;
  onSelect: () => void;
  onHover: () => void;
  locale: Locale;
  t: (key: string, vars?: Record<string, string | number>) => string;
}) {
  const p = hit.payload as Record<string, any>;

  return (
    <button
      type="button"
      id={`hit-${hit.id}`}
      role="option"
      aria-selected={active}
      className={`hit${active ? ' hit--active' : ''}`}
      onClick={onSelect}
      onMouseEnter={onHover}
    >
      <span className={`hit__glyph hit__glyph--${hit.kind}`} aria-hidden="true">
        <KindGlyph kind={hit.kind} />
      </span>

      <span className="hit__body">
        <span className="hit__title">{hit.title}</span>
        {hit.subtitle && <span className="hit__subtitle">{hit.subtitle}</span>}
      </span>

      <span className="hit__aside">
        {hit.kind === 'invoice' && p.balance != null && Number(p.balance) > 0 && (
          <span className="hit__amount hit__amount--due money">{formatMoney(Math.round(Number(p.balance) * 100), locale)}</span>
        )}
        {hit.kind === 'invoice' && Number(p.balance ?? 0) <= 0 && p.total != null && (
          <span className="hit__amount money">{formatMoney(Math.round(Number(p.total) * 100), locale)}</span>
        )}
        {hit.kind === 'quote' && p.total != null && (
          <span className="hit__amount money">{formatMoney(Math.round(Number(p.total) * 100), locale)}</span>
        )}
        {hit.kind === 'job' && p.stage && (
          <span className="hit__chip" style={{ ['--chip' as string]: p.stageColor ?? 'var(--accent)' }}>
            {typeof p.stage === 'object' ? p.stage[locale] ?? p.stage.fr : p.stage}
          </span>
        )}
        {hit.kind === 'call' && (
          <span className="hit__meta">
            {t(`calls.${p.direction === 'inbound' ? 'inbound' : 'outbound'}`)} ·{' '}
            {Math.round((p.durationSeconds ?? 0) / 60)} min
          </span>
        )}
        {/* Why this row matched, when it was not the obvious thing. Typing a phone number
            and getting a name back is confusing without it. */}
        {hit.matchedOn && hit.matchedOn !== 'name' && (
          <span className="hit__matched label">{t(`search.matchedOn.${hit.matchedOn}`)}</span>
        )}
      </span>
    </button>
  );
}

function KindGlyph({ kind }: { kind: SearchKind }) {
  const common = { width: 15, height: 15, viewBox: '0 0 16 16', fill: 'none', stroke: 'currentColor', strokeWidth: 1.5 } as const;
  switch (kind) {
    case 'customer':
      return (
        <svg {...common}>
          <circle cx="8" cy="5.5" r="2.5" />
          <path d="M2.5 14c0-2.8 2.5-4.5 5.5-4.5s5.5 1.7 5.5 4.5" />
        </svg>
      );
    case 'job':
      // A duct elbow: the trade's own shape.
      return (
        <svg {...common}>
          <path d="M2 5h6a3 3 0 0 1 3 3v6" />
          <path d="M2 11h4a1 1 0 0 0 1-1V8" strokeOpacity=".5" />
        </svg>
      );
    case 'quote':
      return (
        <svg {...common}>
          <path d="M4 2h6l3 3v9H4z" />
          <path d="M6 8h5M6 11h3" strokeOpacity=".6" />
        </svg>
      );
    case 'invoice':
      return (
        <svg {...common}>
          <path d="M4 2h8v12l-2-1.3-2 1.3-2-1.3L4 14z" />
          <path d="M6.5 6h3M6.5 9h3" strokeOpacity=".6" />
        </svg>
      );
    case 'call':
      return (
        <svg {...common}>
          <path d="M3.5 2.5h2l1 3-1.5 1a7 7 0 0 0 3.5 3.5l1-1.5 3 1v2a1 1 0 0 1-1 1A10.5 10.5 0 0 1 2.5 3.5a1 1 0 0 1 1-1z" />
        </svg>
      );
    case 'message':
      return (
        <svg {...common}>
          <path d="M2.5 3.5h11v7h-6l-3 2.5v-2.5h-2z" />
        </svg>
      );
  }
}

function SearchIcon() {
  return (
    <svg width={15} height={15} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={1.6} aria-hidden="true">
      <circle cx="6.8" cy="6.8" r="4.3" />
      <path d="M10 10l3.5 3.5" />
    </svg>
  );
}

function PinIcon() {
  return (
    <svg width={13} height={13} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={1.6} aria-hidden="true">
      <path d="M8 14s4.5-4.2 4.5-7.5a4.5 4.5 0 1 0-9 0C3.5 9.8 8 14 8 14z" />
      <circle cx="8" cy="6.4" r="1.6" />
    </svg>
  );
}
